import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import Button from './ui/Button'
import SlideToConfirm from './SlideToConfirm'

const MAX_PHOTO_BYTES = 300 * 1024
const visibleViewport = () => ({ width: window.visualViewport?.width || window.innerWidth,
  height: window.visualViewport?.height || window.innerHeight,
  top: window.visualViewport?.offsetTop || 0 })

export default function TimesheetCaptureDialog({ title, slideLabel, children, onClose, onConfirm,
  disabled = false, busy = false, error = '', status = '', retainedPhoto = null, resetKey }) {
  const titleId = useId()
  const [portal] = useState(() => document.createElement('div'))
  const dialogRef = useRef(null)
  const videoRef = useRef(null)
  const streamRef = useRef(null)
  const generationRef = useRef(0)
  const frameTimeoutRef = useRef(null)
  const mountedRef = useRef(false)
  const openedAtRef = useRef(Date.now())
  const closeRef = useRef(onClose)
  const busyRef = useRef(busy)
  closeRef.current = onClose
  busyRef.current = busy
  const [cameraState, setCameraState] = useState('off')
  const [cameraError, setCameraError] = useState('')
  const [viewport, setViewport] = useState(visibleViewport)
  const compactLandscape = viewport.width >= 640 && viewport.height < 600
  const desktopFrame = viewport.height >= 600

  useEffect(() => {
    const update = () => setViewport(visibleViewport())
    const visualViewport = window.visualViewport
    update()
    visualViewport?.addEventListener('resize', update)
    visualViewport?.addEventListener('scroll', update)
    window.addEventListener('resize', update)
    return () => {
      visualViewport?.removeEventListener('resize', update)
      visualViewport?.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  const stopCamera = useCallback(() => {
    ++generationRef.current
    window.clearTimeout(frameTimeoutRef.current)
    const stream = streamRef.current
    streamRef.current = null
    stream?.getTracks().forEach(track => track.stop())
    if (videoRef.current) videoRef.current.srcObject = null
  }, [])

  useEffect(() => {
    mountedRef.current = true
    const previousFocus = document.activeElement
    const scrollY = window.scrollY
    const bodyStyle = { overflow: document.body.style.overflow, position: document.body.style.position,
      top: document.body.style.top, width: document.body.style.width }
    document.body.appendChild(portal)
    const siblings = [...document.body.children].filter(element => element !== portal).map(element => [element, element.inert])
    siblings.forEach(([element]) => { element.inert = true })
    Object.assign(document.body.style, { overflow: 'hidden', position: 'fixed', top: `-${scrollY}px`, width: '100%' })
    const initialFocus = dialogRef.current?.querySelector('input:not(:disabled)') || dialogRef.current
    initialFocus?.focus({ preventScroll: true })
    const keys = (event) => {
      if (event.key === 'Escape') { event.preventDefault(); if (!busyRef.current) closeRef.current(); return }
      if (event.key !== 'Tab') return
      const elements = [...dialogRef.current.querySelectorAll('button:not(:disabled),input:not(:disabled),a[href],[tabindex="0"]')]
        .filter(element => element.getClientRects().length && element.getAttribute('aria-disabled') !== 'true')
      const first = elements[0], last = elements.at(-1)
      if (!first) { event.preventDefault(); dialogRef.current.focus(); return }
      if (event.shiftKey && (document.activeElement === first || !elements.includes(document.activeElement))) {
        event.preventDefault(); last.focus()
      } else if (!event.shiftKey && (document.activeElement === last || !elements.includes(document.activeElement))) {
        event.preventDefault(); first.focus()
      }
    }
    const hidden = () => {
      if (document.visibilityState !== 'visible') { stopCamera(); closeRef.current() }
    }
    document.addEventListener('keydown', keys)
    document.addEventListener('visibilitychange', hidden)
    return () => {
      mountedRef.current = false
      stopCamera()
      document.removeEventListener('keydown', keys)
      document.removeEventListener('visibilitychange', hidden)
      siblings.forEach(([element, wasInert]) => { element.inert = wasInert })
      Object.assign(document.body.style, bodyStyle)
      portal.remove()
      window.scrollTo(0, scrollY)
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true })
    }
  }, [portal, stopCamera])

  const enableCamera = useCallback(async () => {
    stopCamera(); setCameraError('')
    if (!window.isSecureContext) { setCameraState('off'); setCameraError('Open the secure HTTPS app link to use your camera.'); return }
    if (!navigator.mediaDevices?.getUserMedia) { setCameraState('off'); setCameraError('This browser cannot open the camera. Open the app in Safari and try again.'); return }
    const generation = generationRef.current
    setCameraState('starting')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
      if (!mountedRef.current || generation !== generationRef.current || document.visibilityState !== 'visible') {
        stream.getTracks().forEach(track => track.stop()); return
      }
      streamRef.current = stream
      const ended = () => {
        if (!mountedRef.current || streamRef.current !== stream) return
        stopCamera(); setCameraState('off'); setCameraError('The camera stopped. Retry the camera before recording your time.')
      }
      stream.getVideoTracks().forEach(track => track.addEventListener('ended', ended, { once: true }))
      if (!stream.getVideoTracks().some(track => track.readyState === 'live' && track.enabled)) throw new Error('No live camera')
      const video = videoRef.current
      video.srcObject = stream
      frameTimeoutRef.current = window.setTimeout(() => {
        if (mountedRef.current && generation === generationRef.current && (!video.videoWidth || !video.videoHeight || video.readyState < 2)) {
          stopCamera(); setCameraState('off'); setCameraError('No camera image arrived. Retry the camera to continue.')
        }
      }, 8000)
      await video.play()
      if (!mountedRef.current || generation !== generationRef.current) return
      if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) setCameraState('ready')
    } catch (err) {
      if (!mountedRef.current || generation !== generationRef.current) return
      stopCamera(); setCameraState('off')
      setCameraError(['NotAllowedError', 'PermissionDeniedError'].includes(err?.name)
        ? 'Camera access was denied. Allow camera access for this app in Safari, then try again.'
        : 'The camera could not start. Check that it is available, then try again.')
    }
  }, [stopCamera])

  // Opening a punch dialog is the employee's request to use the camera.
  // Reuse the original photo for uncertain-result retries instead of retaking it.
  const hasRetainedPhoto = Boolean(retainedPhoto)
  useEffect(() => {
    if (hasRetainedPhoto) { stopCamera(); setCameraState('off') }
    else void enableCamera()
    return stopCamera
  }, [hasRetainedPhoto, enableCamera, stopCamera])

  const captureAndConfirm = () => {
    if (disabled || busy || document.visibilityState !== 'visible') return
    if (retainedPhoto) { onConfirm(retainedPhoto); return }
    const video = videoRef.current
    const live = streamRef.current?.getVideoTracks().some(track => track.readyState === 'live' && track.enabled && !track.muted)
    if (!live || !video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      stopCamera(); setCameraState('off'); setCameraError('There is no live camera image. Retry the camera and slide again.'); return
    }
    try {
      const scale = Math.min(1, 640 / Math.max(video.videoWidth, video.videoHeight))
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale))
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale))
      const context = canvas.getContext('2d')
      if (!context) throw new Error('Image unavailable')
      context.drawImage(video, 0, 0, canvas.width, canvas.height)
      let dataUrl = ''
      for (const quality of [0.72, 0.6, 0.45, 0.3]) {
        dataUrl = canvas.toDataURL('image/jpeg', quality)
        if (dataUrl.startsWith('data:image/jpeg;base64,') && Math.ceil(dataUrl.split(',')[1].length * 3 / 4) <= MAX_PHOTO_BYTES) break
      }
      if (!dataUrl.startsWith('data:image/jpeg;base64,') || Math.ceil(dataUrl.split(',')[1].length * 3 / 4) > MAX_PHOTO_BYTES) throw new Error('Image too large')
      const capturedAt = Date.now()
      if (capturedAt < openedAtRef.current) throw new Error('Camera time changed')
      onConfirm({ mimeType: 'image/jpeg', dataUrl, capturedAt: new Date(capturedAt).toISOString() })
    } catch {
      setCameraError('The photo could not be captured. Check the live preview and slide again.')
    }
  }

  return createPortal(<div className={`fixed inset-x-0 z-[1000] flex w-full items-center justify-center bg-black/60 ${desktopFrame ? 'sm:p-6' : ''}`}
    style={{ top: viewport.top, height: viewport.height }}>
    <section ref={dialogRef} role="dialog" aria-modal="true" aria-busy={busy} aria-labelledby={titleId} tabIndex={-1}
      className={`h-full w-full overflow-hidden bg-background text-foreground outline-none ${compactLandscape
        ? 'grid grid-cols-[minmax(0,1fr)_minmax(280px,0.75fr)] grid-rows-[auto_minmax(0,1fr)]'
        : 'flex flex-col'} ${desktopFrame ? 'sm:max-h-[900px] sm:max-w-lg sm:rounded-2xl' : ''}`}
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}>
      <header className="col-span-2 flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3">
        <h2 id={titleId} className="text-xl font-semibold">{title}</h2>
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy} aria-label="Close confirmation" iconName="X">Close</Button>
      </header>
      <div data-testid="timesheet-dialog-body" className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto overscroll-contain p-4">
        <div className="shrink-0 space-y-4">{children}</div>
        <div data-testid="timesheet-camera-frame" className="relative min-h-[120px] w-full flex-1 overflow-hidden rounded-xl bg-black">
          <video ref={videoRef} aria-label="Live camera preview" autoPlay playsInline muted
            className={`absolute inset-0 h-full w-full object-cover ${retainedPhoto ? 'hidden' : ''}`} style={{ transform: 'scaleX(-1)' }}
            onLoadedData={() => { if (mountedRef.current && streamRef.current && videoRef.current?.videoWidth && videoRef.current?.videoHeight) { window.clearTimeout(frameTimeoutRef.current); setCameraState('ready') } }} />
          {retainedPhoto && <img src={retainedPhoto.dataUrl} alt="Photo captured for this request" className="absolute inset-0 h-full w-full object-cover" />}
          {!retainedPhoto && cameraState !== 'ready' && <p className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-white/80">{cameraError ? 'Camera unavailable' : 'Starting camera…'}</p>}
        </div>
        <div className="shrink-0 space-y-3">
          {!retainedPhoto && cameraError && <Button type="button" variant="outline" iconName="Camera" onClick={enableCamera} disabled={busy || cameraState === 'starting'}>Retry camera</Button>}
          <p className="text-xs leading-relaxed text-muted-foreground">{retainedPhoto ? 'The same photo will be used to check the result of this request.' : 'A photo is taken when you finish sliding and saved with this action for Cinara to review.'}</p>
          {cameraError && <p role="alert" className="text-sm text-destructive">{cameraError}</p>}
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          {status && <p role="status" className="text-sm text-muted-foreground">{status}</p>}
        </div>
      </div>
      <footer className={`min-w-0 shrink-0 border-border bg-background p-4 ${compactLandscape ? 'flex min-h-0 flex-col overflow-y-auto overscroll-contain border-l' : 'border-t'}`}>
        <div className={compactLandscape ? 'my-auto shrink-0' : undefined}>
          <SlideToConfirm label={slideLabel} onConfirm={captureAndConfirm} resetKey={`${resetKey}:${cameraState}`}
            disabled={disabled || busy || (!retainedPhoto && cameraState !== 'ready')} />
        </div>
      </footer>
    </section>
  </div>, portal)
}
